import React from "react";
import { AlertTriangle, CheckCircle, CreditCard } from "lucide-react";
import { formatCompareAnalysisCopy, isTargetedMode, primaryConfirmLabel } from "./sourceComparePreflightCopy";
import "./RepositoryAnalysisConfirmation.css";

export default function RepositoryAnalysisConfirmation({
  estimate,
  loading = false,
  onConfirm,
  onCancel,
  onPurchase,
  compact = false,
  prPreflight = null,
  confirmLabel = null,
}) {
  if (!estimate) return null;

  const selectedFiles = estimate.selectedFiles ?? estimate.eligibleFiles ?? 0;
  const eligibleFiles = estimate.eligibleBeforeCap ?? estimate.eligibleFiles ?? 0;

  return (
    <section
      className={`analysis-confirmation${compact ? " compact" : ""}`}
      aria-label="Repository analysis cost preview"
    >
      <div className="analysis-confirmation-header">
        <CreditCard size={20} aria-hidden="true" />
        <h4>Cost preview</h4>
      </div>

      <div className="analysis-confirmation-summary">
        <div className="analysis-confirmation-main">
          <span className="analysis-confirmation-credits">
            {Number(estimate.estimatedCredits || 0).toLocaleString()} credits
          </span>
          <span className="analysis-confirmation-files">
            {selectedFiles} of {estimate.totalFiles ?? eligibleFiles} files selected
            {estimate.fileCapApplied && eligibleFiles > selectedFiles
              ? ` (${eligibleFiles} eligible before the analysis cap)`
              : ""}
          </span>
        </div>

        <div
          className={`analysis-confirmation-status ${
            estimate.userHasEnoughCredits ? "sufficient" : "insufficient"
          }`}
        >
          {estimate.userHasEnoughCredits ? (
            <>
              <CheckCircle size={16} aria-hidden="true" />
              Sufficient credits
            </>
          ) : (
            <>
              <AlertTriangle size={16} aria-hidden="true" />
              Insufficient credits
            </>
          )}
        </div>
      </div>

      {prPreflight && (
        <p className="analysis-confirmation-note" data-testid="pr-analysis-mode">
          {isTargetedMode(prPreflight.mode)
            ? `Targeted change review. ${formatCompareAnalysisCopy(prPreflight)} Estimated credits are based on the bounded changed-plus-related file set.`
            : prPreflight.mode === "verified_incremental" || prPreflight.mode === "incremental"
              ? `Verified incremental analysis of this source. ${formatCompareAnalysisCopy(prPreflight)}`
              : `Verified full snapshot of the head at ${prPreflight.headPin?.sha?.slice(0, 12) || "the pinned commit"}. ${formatCompareAnalysisCopy(prPreflight)}`}
        </p>
      )}

      <p className="analysis-confirmation-note">
        Credits are charged from actual model usage. This preview authorizes only
        these settings and this repository snapshot.
      </p>

      <div className="analysis-confirmation-actions">
        {estimate.userHasEnoughCredits ? (
          <button
            type="button"
            className="btn-primary"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading
              ? "Starting analysis…"
              : confirmLabel || primaryConfirmLabel(prPreflight)}
          </button>
        ) : (
          <button type="button" className="btn-secondary" onClick={onPurchase}>
            Purchase Credits
          </button>
        )}
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}
